// src/autodesk_usage/components/corporate/ActiveLicenseUsageChart.tsx
// All comments in English only
import { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCorporateMonthlyUsage } from "@/autodesk_usage/hooks/useCorporateMonthlyUsage";
import { useCorporateProductUsageByMonth } from "@/autodesk_usage/hooks/useCorporateProductUsageByMonth";
import { useCompanyMonthlyUsage } from "@/autodesk_usage/hooks/useCompanyMonthlyUsage";
import { useCompanyProductUsageByMonth } from "@/autodesk_usage/hooks/useCompanyProductUsageByMonth";

export type UsageMode = "yearly" | "monthly" | "quarterly" | "date_range";
export type UsageScope = "corporate" | "company";

interface Props {
  scope: UsageScope;
  entityName: string | string[];
  title: string;
  mode: UsageMode;
  startMonth: string;
  endMonth: string;
}

type MonthlyRow = {
  usage_month: string;
  used_users: number;
  active_seats: number;
};

type ProductRow = MonthlyRow & {
  product: string;
};

type PeriodProductRow = {
  product: string;
  used_users: number;
  active_seats: number;
};

type PeriodRow = {
  label: string;
  sortKey: number;
  used_users: number;
  active_seats: number;
  gap: number;
  products: PeriodProductRow[];
};

const formatNumber = (value: number | null | undefined) =>
  (value ?? 0).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });

const parseMonth = (value: string) => new Date(`${value.slice(0, 10)}T00:00:00Z`);

const formatMonthKey = (date: Date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const getQuarterInfo = (date: Date) => {
  const year = date.getUTCFullYear();
  const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
  const quarterStartMonth = (quarter - 1) * 3;
  return {
    label: `${year} Q${quarter}`,
    sortKey: Date.UTC(year, quarterStartMonth, 1),
  };
};

const getModeLabel = (mode: UsageMode) => {
  switch (mode) {
    case "yearly":
      return "Yearly";
    case "quarterly":
      return "Quarterly";
    case "date_range":
      return "Date range";
    case "monthly":
    default:
      return "Monthly";
  }
};

const getModePeriod = (date: Date, mode: UsageMode) => {
  if (mode === "yearly") {
    const year = date.getUTCFullYear();
    return {
      label: `${year}`,
      sortKey: Date.UTC(year, 0, 1),
    };
  }
  if (mode === "quarterly") {
    return getQuarterInfo(date);
  }
  const key = formatMonthKey(date);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  return {
    label: key,
    sortKey: Date.UTC(year, month, 1),
  };
};

const aggregateRows = (
  monthlyRows: MonthlyRow[],
  productRows: ProductRow[],
  mode: UsageMode
): PeriodRow[] => {
  const periods = new Map<string, PeriodRow & { productsMap: Map<string, PeriodProductRow> }>();

  const ensurePeriod = (label: string, sortKey: number) => {
    const existing = periods.get(label);
    if (existing) return existing;
    const created = {
      label,
      sortKey,
      used_users: 0,
      active_seats: 0,
      gap: 0,
      products: [],
      productsMap: new Map<string, PeriodProductRow>(),
    };
    periods.set(label, created);
    return created;
  };

  const mergeMetric = (current: number, incoming: number) =>
    Math.max(current, incoming);

  for (const row of monthlyRows) {
    const date = parseMonth(String(row.usage_month));
    const period = getModePeriod(date, mode);
    const entry = ensurePeriod(period.label, period.sortKey);
    entry.used_users = mergeMetric(entry.used_users, Number(row.used_users || 0));
    entry.active_seats = mergeMetric(entry.active_seats, Number(row.active_seats || 0));
  }

  for (const row of productRows) {
    const date = parseMonth(String(row.usage_month));
    const period = getModePeriod(date, mode);
    const entry = ensurePeriod(period.label, period.sortKey);
    const productKey = row.product;
    const existing = entry.productsMap.get(productKey) ?? {
      product: productKey,
      used_users: 0,
      active_seats: 0,
    };
    existing.used_users = mergeMetric(existing.used_users, Number(row.used_users || 0));
    existing.active_seats = mergeMetric(existing.active_seats, Number(row.active_seats || 0));
    entry.productsMap.set(productKey, existing);
  }

  return Array.from(periods.values())
    .sort((a, b) => a.sortKey - b.sortKey)
    .map((entry) => {
      const products = Array.from(entry.productsMap.values()).sort(
        (a, b) =>
          b.used_users - a.used_users ||
          a.product.localeCompare(b.product)
      );
      return {
        label: entry.label,
        sortKey: entry.sortKey,
        used_users: entry.used_users,
        active_seats: entry.active_seats,
        gap: entry.used_users - entry.active_seats,
        products,
      };
    });
};

const formatTooltipTitle = (label: string, mode: UsageMode) => {
  if (mode === "yearly" || mode === "quarterly") {
    return label;
  }
  return label;
};

export default function ActiveLicenseUsageChart({
  scope,
  entityName,
  title,
  mode,
  startMonth,
  endMonth,
}: Props) {
  const corporateMonthly = useCorporateMonthlyUsage({
    corporateName: scope === "corporate" ? entityName : undefined,
    startMonth,
    endMonth,
  });
  const companyMonthly = useCompanyMonthlyUsage({
    companyName: scope === "company" ? entityName : undefined,
    startMonth,
    endMonth,
  });

  const corporateProducts = useCorporateProductUsageByMonth({
    corporateName: scope === "corporate" ? entityName : undefined,
    startMonth,
    endMonth,
  });
  const companyProducts = useCompanyProductUsageByMonth({
    companyName: scope === "company" ? entityName : undefined,
    startMonth,
    endMonth,
  });

  const monthlyData = scope === "corporate" ? corporateMonthly.data : companyMonthly.data;
  const isLoading = scope === "corporate" ? corporateMonthly.isLoading : companyMonthly.isLoading;
  const error = scope === "corporate" ? corporateMonthly.error : companyMonthly.error;
  const productData = scope === "corporate" ? corporateProducts.data : companyProducts.data;
  const productError = scope === "corporate" ? corporateProducts.error : companyProducts.error;
  const productLoading = scope === "corporate" ? corporateProducts.isLoading : companyProducts.isLoading;
  const entityCount = Array.isArray(entityName) ? entityName.length : entityName ? 1 : 0;

  const rows = useMemo(
    () => aggregateRows((monthlyData ?? []) as MonthlyRow[], (productData ?? []) as ProductRow[], mode),
    [monthlyData, productData, mode]
  );

  const subtitle =
    entityCount > 1
      ? `${getModeLabel(mode)} view · aggregated across ${entityCount} companies`
      : `${getModeLabel(mode)} view`;

  if (isLoading) {
    return (
      <Card className="border shadow-sm">
        <CardContent className="p-6 text-sm text-muted-foreground">
          Loading chart...
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border shadow-sm">
        <CardContent className="p-6 text-sm text-destructive">
          Failed to load chart.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border shadow-sm">
      <CardHeader>
        <CardTitle>
          {title} ({getModeLabel(mode)})
        </CardTitle>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </CardHeader>
      <CardContent className="h-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ left: 8, right: 16, top: 16, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" minTickGap={24} />
            <YAxis />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0].payload as PeriodRow;
                return (
                  <div className="rounded-md border bg-background p-3 text-xs shadow-sm space-y-2 w-72">
                    <div className="text-sm font-semibold">
                      {formatTooltipTitle(String(label), mode)}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span style={{ color: "#ef4444" }}>
                          Active Licenses
                        </span>
                        <span className="font-medium">
                          {formatNumber(row?.used_users)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span style={{ color: "#3b82f6" }}>Forida</span>
                        <span className="font-medium">
                          {formatNumber(row?.active_seats)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span style={{ color: "#f59e0b" }}>Gap</span>
                        <span className="font-medium">
                          {formatNumber(row?.gap)}
                        </span>
                      </div>
                    </div>
                    <div className="border-t pt-2">
                      <div className="text-xs font-semibold text-muted-foreground mb-1">
                        Products
                      </div>
                      {productLoading ? (
                        <div className="text-muted-foreground">Loading...</div>
                      ) : productError ? (
                        <div className="text-muted-foreground">-</div>
                      ) : row?.products?.length ? (
                        <div className="space-y-1">
                          {row.products.map((product) => (
                            <div
                              key={product.product}
                              className="flex items-center justify-between gap-2"
                            >
                              <span className="truncate">{product.product}</span>
                              <span className="text-muted-foreground shrink-0">
                                {product.used_users} ({product.active_seats})
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted-foreground">-</div>
                      )}
                    </div>
                  </div>
                );
              }}
            />
            <Legend
              formatter={(value: any, entry: any) => {
                const label = String(value);
                const swappedColor =
                  label === "Active Licenses"
                    ? "#ef4444"
                    : label === "Forida"
                    ? "#3b82f6"
                    : label === "Gap"
                    ? "#f59e0b"
                    : entry?.color;
                return <span style={{ color: swappedColor }}>{label}</span>;
              }}
            />
            <Bar
              dataKey="gap"
              name="Gap"
              fill="#f59e0b"
              barSize={16}
              radius={[4, 4, 0, 0]}
            />
            <Line
              type="monotone"
              dataKey="used_users"
              name="Active Licenses"
              stroke="#ef4444"
              strokeWidth={2.5}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="active_seats"
              name="Forida"
              stroke="#3b82f6"
              strokeWidth={2.5}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
